'use client';

import { IdleSessionHandler } from '@storefront/app/(purchase)/checkout/session-info/idle-session-handler/idle-session-handler';
import {
  getAemCallbackURL,
  getCurrentProductConfigurationURL,
  getFallbackURL,
} from '@storefront/services/SessionHelperService/sessionHelperService';
import ErrorInformation from '@storefront/app/v2/shared-components/error-information-v2/error-information';
import { HeldError } from '@storefront/shared-components/error-information/held-error';
import { RetryableError } from '@storefront/shared-components/error-information/retryable-error';
import TranslatedText from '@storefront/shared-components/translated-text/translated-text';
import sendOpenTelemetryEvent from '@storefront/utils/sendOpenTelemetryEvent';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { NamespacesI18n } from '@storefront/i18n';
import { useSession } from 'next-auth/react';
import { buildPath } from '@storefront/utils/utils';
import { logoutAuthz } from '@storefront/actions/logoutAuthz';
import { signInHandler } from '../../api/auth/[...nextauth]/auth.client.utils';

type ErrorProps = {
  error: RetryableError | HeldError;
  resetErrorBoundary?: () => unknown;
};

export default function ErrorClientWrapperVisID({
  error,
}: Readonly<ErrorProps>) {
  const i18nPath = error.i18nPath
    ? error.i18nPath
    : 'errors.SOMETHING_WENT_WRONG';

  const camelCaseErrorId = (i18nPath: string) =>
    i18nPath
      ?.replace('errors.', '')
      ?.split('_')
      ?.map(
        (item: string) =>
          item?.charAt(0)?.toUpperCase() + item?.slice(1)?.toLowerCase()
      )
      .join('');

  const { t, i18n } = useTranslation(NamespacesI18n.COMMON, {
    keyPrefix: i18nPath,
  });
  const pathname = usePathname();

  // used to filter v2's routes that do not require idle session handler
  const noSessionRequiredRoutes = ['/cart', '/pdp-config'];

  if (
    i18nPath === 'errors.CANNOT_ACCESS_CART' ||
    i18nPath === 'errors.CANNOT_ACCESS_ADDRESS'
  ) {
    const name =
      i18nPath === 'errors.CANNOT_ACCESS_CART'
        ? 'cannot-access-cart'
        : 'cannot-access-customer-address';
    sendOpenTelemetryEvent(name, JSON.stringify(error));
  }

  const translatedComponents = {
    accountDashboard: {
      href: process.env['NEXT_PUBLIC_MY_ACCOUNT'],
      id: 'error-account-dashboard-link',
    },
  };

  const description = (
    <TranslatedText
      underline={true}
      i18nKey={t('description')}
      links={translatedComponents}
      linkStyle="black"
    />
  );
  const { data: sessionData } = useSession();
  const id_token = sessionData?.id_token;
  const searchParams = useSearchParams();
  const cartId = searchParams?.get('cartId');
  const errorId = camelCaseErrorId(i18nPath);

  const handleSignOutClick = async () => {
    const redirectPath = buildPath(`/claim?cartId=${cartId}`);
    await logoutAuthz(id_token, redirectPath);
    await signInHandler({
      path: redirectPath,
      authorizationParams: {
        prompt: 'login',
      },
    });
  };

  return (
    <>
      <ErrorInformation
        errorId={camelCaseErrorId(i18nPath)}
        code={t(`code`)}
        title={t(`title`)}
        description={description}
        {...(i18n.exists(`${i18nPath}.description2`) && {
          description2: t(`description2`),
        })}
        {...(errorId === 'EmailDomainNotSupported' && {
          handleClick: handleSignOutClick,
        })}
        buttonDetails={
          i18n.exists(`${i18nPath}.redirectUrl`) && {
            redirectToURL: t('redirectUrl', {
              aemURL: getAemCallbackURL() ?? getFallbackURL(),
              hpStoreURL: getFallbackURL(),
            }),
            label: t('buttonLabel'),
          }
        }
        redirectLink={
          i18n.exists(`${i18nPath}.redirectLink`) && {
            title: t('redirectTitle'),
            link: t('redirectLink', {
              currentProductConfigurationURL:
                getCurrentProductConfigurationURL() ?? getFallbackURL(),
            }),
          }
        }
      />
      {!noSessionRequiredRoutes.includes(pathname) && <IdleSessionHandler />}
    </>
  );
}
